/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { UnityRootState } from "../UnityAppStore";
import type {
  AlertOverlayAction,
  AlertOverlayConfig,
  AlertOverlayState,
  AlertOverlayVariant
} from "./alertOverlay.types";

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Required by CodingGuidelines §7 (D-09)
const TAG: string = "alertOverlaySlice";

const DEFAULT_OK_ACTION = { id: "ok", label: "OK" };

const INITIAL_STATE: AlertOverlayState = {
  isVisible: false,
  title: "",
  message: "",
  actions: [DEFAULT_OK_ACTION],
  dismissedByActionId: "",
  selectedActionId: "",
  retryPending: false,
  focusedActionIndex: 0,
  owner: "",
  variant: "default"
};

/**
 * Whether a `showAlertOverlay` payload carries exactly the same buttons, in the
 * same order, as the overlay currently on screen. Used to decide whether a
 * re-show is a refresh of the same overlay (keep the focused button) or a new
 * one (focus the first button).
 */
function hasSameActionIds(
  current: Array<AlertOverlayAction>,
  payload: AlertOverlayConfig
): boolean {
  const next = payload.actions;
  if (next === undefined || next === null) {
    return current.length === 1 && current[0].id === DEFAULT_OK_ACTION.id;
  }
  return (
    next.length === current.length &&
    next.every(
      (action: AlertOverlayAction, index: number): boolean =>
        action.id === current[index].id
    )
  );
}

const alertOverlaySlice = createSlice({
  name: "alertOverlay",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Show the alert overlay with the given configuration.
     * If no actions are provided, a single "OK" button is used.
     *
     * A CRITICAL retry loop re-dispatches this on every failed attempt to
     * refresh the error message, so an overlay that is already up with the same
     * buttons keeps the focus the user moved — resetting it would drag focus
     * back to the first button on each backoff tick, mid-navigation.
     */
    showAlertOverlay(state, action: PayloadAction<AlertOverlayConfig>): void {
      const wasShowingSameActions: boolean =
        state.isVisible && hasSameActionIds(state.actions, action.payload);
      state.isVisible = true;
      state.title = action.payload.title;
      state.message = action.payload.message;
      const payloadActions = action.payload.actions;
      if (
        payloadActions !== undefined &&
        payloadActions !== null &&
        payloadActions.length > 0
      ) {
        state.actions = payloadActions;
      } else if (payloadActions === undefined || payloadActions === null) {
        state.actions = [DEFAULT_OK_ACTION];
      } else {
        state.actions = [];
      }
      state.dismissedByActionId = "";
      state.selectedActionId = "";
      state.retryPending = false;
      state.focusedActionIndex = wasShowingSameActions
        ? state.focusedActionIndex
        : 0;
      const owner = action.payload.owner;
      state.owner = owner !== undefined && owner !== null ? owner : "";
      const variant = action.payload.variant;
      state.variant =
        variant !== undefined && variant !== null ? variant : "default";
    },

    /**
     * Record a `keepsOverlayOpen` action the user selected, without hiding the
     * overlay. Ignored while a retry is already pending so repeated presses
     * cannot queue duplicate work.
     */
    alertOverlayActionSelected(state, action: PayloadAction<string>): void {
      if (state.retryPending) {
        return;
      }
      state.selectedActionId = action.payload;
    },

    /**
     * Set whether a manual retry is in flight. Drives the Retry button's
     * disabled/loading state.
     */
    setRetryPending(state, action: PayloadAction<boolean>): void {
      state.retryPending = action.payload;
    },

    /**
     * Dismiss the alert overlay and record which action was clicked.
     */
    dismissAlertOverlay(state, action: PayloadAction<string>): void {
      state.isVisible = false;
      state.dismissedByActionId = action.payload;
      state.selectedActionId = "";
      state.retryPending = false;
      state.owner = "";
    },

    /**
     * Dismiss the overlay only if it is still the one `owner` raised.
     *
     * The overlay is one global slot, so an unguarded `dismissAlertOverlay`
     * from a background task clears whatever is currently showing — including
     * another subsystem's blocking overlay that is still holding the screen.
     * The concrete failure: post-sign-in `deviceFeatures` is down and blocking
     * behind its CRITICAL-retry overlay, a TSRI endpoint recovery for some
     * screen behind it succeeds, and its dismiss unblocks a Home screen that
     * has no `msoServiceId` and cannot work. `deviceFeatures` would not
     * re-raise until its next retry — up to 60s later, and longer as the
     * CRITICAL ladder backs off.
     *
     * A dismiss whose owner no longer matches is a no-op: the newer overlay
     * outranks it, and its own owner will clear it.
     */
    dismissAlertOverlayIfOwned(
      state,
      action: PayloadAction<{ owner: string; dismissedByActionId: string }>
    ): void {
      if (state.owner !== action.payload.owner) {
        return;
      }
      state.isVisible = false;
      state.dismissedByActionId = action.payload.dismissedByActionId;
      state.selectedActionId = "";
      state.retryPending = false;
      state.owner = "";
    },

    /**
     * Reset the alert overlay state to initial values.
     */
    resetAlertOverlay(state): void {
      state.isVisible = INITIAL_STATE.isVisible;
      state.title = INITIAL_STATE.title;
      state.message = INITIAL_STATE.message;
      state.actions = INITIAL_STATE.actions;
      state.dismissedByActionId = INITIAL_STATE.dismissedByActionId;
      state.selectedActionId = INITIAL_STATE.selectedActionId;
      state.retryPending = INITIAL_STATE.retryPending;
      state.focusedActionIndex = INITIAL_STATE.focusedActionIndex;
      state.owner = INITIAL_STATE.owner;
      state.variant = INITIAL_STATE.variant;
    },

    /**
     * Move D-pad focus to the next action button.
     */
    focusNextAction(state): void {
      if (state.focusedActionIndex < state.actions.length - 1) {
        state.focusedActionIndex = state.focusedActionIndex + 1;
      }
    },

    /**
     * Move D-pad focus to the previous action button.
     */
    focusPrevAction(state): void {
      if (state.focusedActionIndex > 0) {
        state.focusedActionIndex = state.focusedActionIndex - 1;
      }
    }
  }
});

/* ── Actions ──────────────────────────────────────────────────────────── */
export const {
  showAlertOverlay,
  dismissAlertOverlay,
  dismissAlertOverlayIfOwned,
  alertOverlayActionSelected,
  setRetryPending,
  resetAlertOverlay,
  focusNextAction,
  focusPrevAction
} = alertOverlaySlice.actions;

/* ── Selectors ────────────────────────────────────────────────────────── */
export const selectIsAlertOverlayVisible = (state: UnityRootState): boolean =>
  state.alertOverlay.isVisible;

export const selectAlertOverlayTitle = (state: UnityRootState): string =>
  state.alertOverlay.title;

export const selectAlertOverlayMessage = (state: UnityRootState): string =>
  state.alertOverlay.message;

export const selectAlertOverlayActions = (
  state: UnityRootState
): Array<AlertOverlayAction> => state.alertOverlay.actions;

export const selectDismissedByActionId = (state: UnityRootState): string =>
  state.alertOverlay.dismissedByActionId;

export const selectAlertOverlaySelectedActionId = (
  state: UnityRootState
): string => state.alertOverlay.selectedActionId;

export const selectAlertOverlayRetryPending = (
  state: UnityRootState
): boolean => state.alertOverlay.retryPending;

export const selectFocusedActionIndex = (state: UnityRootState): number =>
  state.alertOverlay.focusedActionIndex;

export const selectAlertOverlayOwner = (state: UnityRootState): string =>
  state.alertOverlay.owner;

export const selectAlertOverlayVariant = (
  state: UnityRootState
): AlertOverlayVariant => {
  const variant = state.alertOverlay.variant;
  return variant !== undefined && variant !== null ? variant : "default";
};

export default alertOverlaySlice.reducer;
